import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WorkOrderDetailsService } from 'src/app/work-order/work-order-details.service';
import { WorkOrderDocumentService } from '@app/work-order/documents/work-order-document.service';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { WorkOrderService } from '@app/work-order/work-order.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoadWorkOrder, LoadDocumentPage, LoadWorkOrderDocuments } from '@dwo/actions';
import { getCurrentUser, getPageForDocument } from '@dwo/selectors';
import { UserDto } from '@app/generated';
import { DocumentDto } from '@app/generated/model';
import { blobToDataUrl } from '@app/utils';

const NOT_AVAILABLE_OFFLINE = 'workOrderDocuments.notAvailableOffline';

@Component({
  selector: 'app-document-preview',
  templateUrl: './document-preview.component.html',
  styleUrls: ['./document-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DocumentPreviewComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private offlineData: OfflineDataDb,
    private workOrderDocumentService: WorkOrderDocumentService,
    private workOrderDetailsService: WorkOrderDetailsService,
    private workOrderService: WorkOrderService,
    private httpClient: HttpClient,
    private store: Store,
  ) {}

  documentId: string;
  loading = true;
  pdfSrc: string | undefined;
  documentError: string | null = null;
  currentPage = 1;
  currentUser: string;
  private destroy$ = new Subject<void>();

  async ngOnInit() {
    // const workOrder = this.workOrderDetailsService.get();
    const workOrderId = this.route.parent?.parent?.snapshot.paramMap.get('id') ?? '';
    this.workOrderService.navigationShowDocumentMenu = true;
    this.workOrderService.navigationBackRoute = `/work-order/${workOrderId}/documents`;

    this.store.dispatch(new LoadWorkOrder(workOrderId));
    this.store.dispatch(new LoadWorkOrderDocuments(workOrderId));

    this.documentId = this.route.snapshot.paramMap.get('document-id') ?? '';

    const document = await this.resolveDocument(workOrderId);
    if (!document) {
      console.warn(`[DocumentPreview] Could not find document ${this.documentId} for work order ${workOrderId}`);
      this.loading = false;
      return;
    }

    const offlineUrl = document.link?.toString();
    if (!offlineUrl) {
      console.warn(`[DocumentPreview] Document ${this.documentId} has no link URL`);
      this.loading = false;
      return;
    }

    const storedFiles = await this.offlineData.workOrderFiles.where('workOrderId').equals(workOrderId).toArray();

    const offlineDocument = storedFiles.find((f) => f.url === offlineUrl) ?? null;

    if (offlineDocument) {
      // blob: URLs can silently fail to render inside the PDF viewer's iframe on iOS/Safari under a service worker
      const blob = new Blob([offlineDocument.data], { type: offlineDocument.type || 'application/pdf' });
      this.pdfSrc = await blobToDataUrl(blob);
      this.loading = false;
    } else {
      this.httpClient
        .get(offlineUrl, { responseType: 'blob' })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (response: Blob | HttpErrorResponse) => {
            if (!(response instanceof Blob)) {
              this.setDocumentError(response);
              this.loading = false;
              return;
            }
            this.pdfSrc = await blobToDataUrl(response);
            this.loading = false;
          },
          error: (err) => {
            console.warn('[DocumentPreview] Failed to load document from network:', err);
            this.setDocumentError(err);
            this.loading = false;
          },
        });
    }
    this.store.pipe(select(getCurrentUser), takeUntil(this.destroy$)).subscribe((user: UserDto) => {
      this.currentUser = user?.username;
      if (!this.currentUser) return;
      this.store
        .pipe(select(getPageForDocument(this.currentUser, this.documentId)), takeUntil(this.destroy$))
        .subscribe((page) => {
          if (page) {
            this.currentPage = page;
          }
        });
    });
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.store.dispatch(new LoadDocumentPage(this.documentId, newPage, this.currentUser));
  }

  onPdfLoadingFailed(error: unknown): void {
    console.warn('[DocumentPreview] PDF viewer failed to load document:', error);
    this.documentError = 'workOrderDocuments.loadFailed';
  }

  get isOfflineError(): boolean {
    return this.documentError === NOT_AVAILABLE_OFFLINE;
  }

  private setDocumentError(err: { status?: number }): void {
    if (!navigator.onLine) {
      this.documentError = NOT_AVAILABLE_OFFLINE;
      return;
    }
    const status = err?.status;
    // a cross-origin 401/403 is often reported as status 0 since CORS hides the real status
    this.documentError =
      status === 401 || status === 403 || status === 0
        ? 'workOrderDocuments.unauthorized'
        : 'workOrderDocuments.loadFailed';
  }

  private async resolveDocument(workOrderId: string): Promise<DocumentDto | null> {
    // Prefer in-memory service data (already initialized by parent route)
    const inMemoryDocs = this.workOrderDocumentService.get();
    if (Array.isArray(inMemoryDocs) && inMemoryDocs.length > 0) {
      const found = inMemoryDocs.find((doc) => doc.id === this.documentId) ?? null;
      return found;
    }

    // Fall back to IndexedDB when service wasn't initialized (e.g. direct navigation)
    const item = await this.offlineData.workOrderDataItems
      .where({ workOrderId, type: 'DOCUMENTS' })
      .first()
      .catch(() => null);

    if (!item?.data) {
      console.warn(`[DocumentPreview] No DOCUMENTS entry in IndexedDB for workOrderId=${workOrderId}`);
      return null;
    }

    const offlineDocs = item.data as DocumentDto[];
    const found = Array.isArray(offlineDocs) ? (offlineDocs.find((doc) => doc.id === this.documentId) ?? null) : null;
    return found;
  }

  ngOnDestroy(): void {
    this.workOrderService.navigationShowDocumentMenu = false;
    this.workOrderService.navigationBackRoute = null;
    this.destroy$.next();
    this.destroy$.complete();
  }
}
